import { useMemo } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { Controller, useFormContext } from 'react-hook-form';

import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ICS_FORM_FAMILY_DESCRIPTIONS,
  ICS_FORM_FAMILY_LABELS,
  SELECTABLE_ICS_FORM_FAMILIES,
  resolveIcsFormFamily,
} from 'components/IAP/formFamily';
import { useAors } from 'hooks/useAors';
import { useOrgEvents } from 'hooks/orgEvents/orgEventHooks';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';

import { IncidentGeoLocationPicker } from '../IncidentGeoLocationPicker';
import {
  addSessionIncidentCategory,
  useIncidentCategoryOptions,
} from '../incidentCategorySession';
import {
  RESPONSE_WORKFLOWS,
  type CreateIncidentFormValues,
  type CreateIncidentPrefill,
} from './createIncidentModel';

export function StepNameLocation({
  prefill,
}: {
  prefill: CreateIncidentPrefill;
}) {
  const aorText = useAorText();
  const { control, register, watch, setValue, formState } =
    useFormContext<CreateIncidentFormValues>();

  const { data: aors = [], isLoading: aorsIsLoading } = useAors();
  const { data: orgEvents = [] } = useOrgEvents();
  const groups = useAppSelector((state) => state.app.groups);
  const currentGroup = useAppSelector(selectGroup);

  const initialAorSelections = prefill.initialAorSelections ?? [];
  const initialIncidentTypes = prefill.initialIncidentTypes ?? [];
  const initialRelatedEvents = prefill.initialRelatedEvents ?? [];

  // Demo prefills can name AORs and types that the org does not have, so they
  // are prepended as options rather than silently dropped from the picker.
  const aorOptions = useMemo(() => {
    const fromDb = aors.map((aor) => ({ value: aor.id, label: aor.name }));
    const existing = new Set(fromDb.map((option) => option.value));
    const extras = initialAorSelections.filter(
      (option) => !existing.has(option.value)
    );
    return [...extras, ...fromDb];
  }, [aors, initialAorSelections]);

  const selectedIncidentTypes = watch('incident_types');
  const incidentTypeOptions = useIncidentCategoryOptions([
    ...initialIncidentTypes,
    ...(selectedIncidentTypes ?? []),
  ]);

  const relatedEventOptions = useMemo(() => {
    const fromDb = orgEvents.map((event) => ({
      value: event.id,
      label: event.name,
    }));
    const existing = new Set(fromDb.map((option) => option.value));
    const extras = initialRelatedEvents
      .filter((event) => !existing.has(event))
      .map((event) => ({ value: event, label: event }));
    return [...extras, ...fromDb];
  }, [orgEvents, initialRelatedEvents]);

  const groupOptions = useMemo(
    () =>
      (groups ?? []).map((group: Group) => ({
        value: group.group_guid,
        label: group.group_name,
      })),
    [groups]
  );

  const selectedFormFamily = resolveIcsFormFamily(watch('ics_form_family'));
  const geometry = watch('location');
  const address = watch('address');

  return (
    <div className="tw:mx-auto tw:max-w-3xl tw:space-y-6">
      <div data-tour="create-incident-name">
        <Field>
          <FieldLabel htmlFor="create-incident-name">Name</FieldLabel>
          <Input
            id="create-incident-name"
            placeholder="e.g. Hurricane Helene Response"
            aria-invalid={!!formState.errors.incident_name}
            {...register('incident_name', { required: true })}
          />
          <FieldDescription>
            Use a clear, neutral placeholder name. You can update it later when
            Incident Command or Unified Command formalizes the incident name.
          </FieldDescription>
        </Field>
      </div>

      <div data-tour="create-incident-category">
        <Controller
          control={control}
          name="incident_types"
          render={({ field }) => (
            <Field>
              <FieldLabel>Incident Category</FieldLabel>
              <MultiIdCombobox
                options={incidentTypeOptions}
                value={field.value}
                onChange={field.onChange}
                onCreate={addSessionIncidentCategory}
                creatable
                createLabel="Add incident category"
                placeholder="Select one or more categories"
              />
            </Field>
          )}
        />
      </div>

      <Controller
        control={control}
        name="related_events"
        render={({ field }) => (
          <Field>
            <FieldLabel>Related Events</FieldLabel>
            <MultiIdCombobox
              options={relatedEventOptions}
              value={field.value}
              onChange={field.onChange}
              placeholder="Related events"
            />
            <FieldDescription>
              Link one or more org Events to this incident.
            </FieldDescription>
          </Field>
        )}
      />

      <div data-tour="create-incident-location" className="tw:space-y-6">
        <Controller
          control={control}
          name="areas_of_responsibility"
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {aorText('Area of Responsibility (AOR)')}
              </FieldLabel>
              <MultiIdCombobox
                options={aorOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder={
                  aorsIsLoading && initialAorSelections.length === 0
                    ? aorText('Loading AORs…')
                    : aorText('Select AORs')
                }
              />
            </Field>
          )}
        />

        <Field>
          <FieldLabel>Location</FieldLabel>
          <FieldDescription>
            Optional. Search for a place, drop a point, or draw the affected
            area.
            {address ? ` Currently: ${address}` : ''}
          </FieldDescription>
          <IncidentGeoLocationPicker
            geometry={geometry ?? undefined}
            address={address}
            onGeometryChange={(next) =>
              setValue('location', next, { shouldDirty: true })
            }
            onAddressChange={(next) =>
              setValue('address', next ?? null, { shouldDirty: true })
            }
          />
        </Field>
      </div>

      <Field>
        <FieldLabel>Response Workflow</FieldLabel>
        <Select value={RESPONSE_WORKFLOWS.ICS} disabled>
          <SelectTrigger className="tw:w-full">
            <SelectValue>Incident Command System</SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            <SelectItem value={RESPONSE_WORKFLOWS.ICS}>
              Incident Command System
            </SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <div data-tour="create-incident-ics-family">
        <Controller
          control={control}
          name="ics_form_family"
          render={({ field }) => (
            <Field>
              <FieldLabel>ICS Form Set</FieldLabel>
              <Select
                value={field.value}
                onValueChange={(next) => field.onChange(String(next))}
                disabled={SELECTABLE_ICS_FORM_FAMILIES.length < 2}
              >
                <SelectTrigger className="tw:w-full">
                  <SelectValue>
                    {ICS_FORM_FAMILY_LABELS[selectedFormFamily]}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {SELECTABLE_ICS_FORM_FAMILIES.map((family) => (
                    <SelectItem key={family} value={family}>
                      {ICS_FORM_FAMILY_LABELS[family]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldDescription>
                {ICS_FORM_FAMILY_DESCRIPTIONS[selectedFormFamily]} This cannot
                be changed after the incident is created.
              </FieldDescription>
            </Field>
          )}
        />
      </div>

      <Controller
        control={control}
        name="sponsor_organization"
        render={({ field }) => (
          <Field>
            <FieldLabel>Sponsor Organization</FieldLabel>
            <Select
              value={field.value}
              onValueChange={(next) => field.onChange(String(next))}
            >
              <SelectTrigger className="tw:w-full">
                <SelectValue>
                  {groupOptions.find((option) => option.value === field.value)
                    ?.label ??
                    currentGroup?.group_name ??
                    'Select an organization'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                {groupOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
      />
    </div>
  );
}
